import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface EquipmentListProps {
  equipment: any[];
  subscription: any;
  onUpdate: () => void;
}

const EquipmentList: React.FC<EquipmentListProps> = ({ equipment, subscription, onUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, equip: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedEquipment(equip);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedEquipment) {
      navigate(`/app/reminders/equipment/edit/${selectedEquipment.id}`);
    }
    handleMenuClose();
  };

  const handleScheduleMaintenance = () => {
    if (selectedEquipment) {
      navigate(`/app/reminders/maintenance/schedule/${selectedEquipment.id}`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedEquipment) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/reminders/equipment/${selectedEquipment.id}`);
      setDeleteDialogOpen(false);
      setSelectedEquipment(null);
      onUpdate();
    } catch (error) {
      console.error('Error deleting equipment:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const getEquipmentIcon = (type: string) => {
    // You can customize icons based on equipment type
    return <BuildIcon />;
  };

  const activeEquipment = equipment.filter(e => e.is_active);
  const canAddMore = subscription?.service_tier === 'premium' || activeEquipment.length < 2;

  if (activeEquipment.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t('reminders.equipment.noEquipment')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('reminders.equipment.addFirst')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/app/reminders/equipment/add')}
        >
          {t('reminders.addReminder')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)' 
        },
        gap: 3,
        maxWidth: '1400px',
        mx: 'auto'
      }}>
        {activeEquipment.map((equip) => (
          <Box key={equip.id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {getEquipmentIcon(equip.equipment_type?.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {equip.brand && equip.model ? (
                          `${equip.brand} ${equip.model}`
                        ) : equip.brand || equip.model || equip.equipment_type?.name || 'Equipment'}
                      </Typography>
                      {equip.brand && equip.model && (
                        <Typography variant="body2" color="text.secondary">
                          {equip.equipment_type?.name}
                        </Typography>
                      )}
                      {equip.serial_number && (
                        <Typography variant="caption" color="text.secondary">
                          SN: {equip.serial_number}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, equip)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {equip.purchase_date && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>{t('reminders.equipment.purchaseDate')}:</strong>{' '}
                      {format(new Date(equip.purchase_date), 'dd/MM/yyyy')}
                    </Typography>
                  )}
                  
                  {equip.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {equip.notes}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Chip
                      size="small"
                      label={t('reminders.equipment.active')}
                      color="success"
                      variant="outlined"
                    />
                    {equip.maintenance_schedules?.length > 0 && (
                      <Chip
                        size="small"
                        icon={<ScheduleIcon />}
                        label={`${equip.maintenance_schedules.length} ${t('reminders.schedules')}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}

        {canAddMore && (
          <Box>
            <Card 
              sx={{ 
                height: '100%', 
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed',
                transition: 'all 0.2s',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                }
              }}
              onClick={() => navigate('/app/reminders/equipment/add')}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <AddIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary">
                  {t('reminders.addReminder')}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {!canAddMore && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2">
            {t('reminders.equipment.limitReached')}
          </Typography>
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          {t('reminders.editEquipment')}
        </MenuItem>
        <MenuItem onClick={handleScheduleMaintenance}>
          <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
          {t('reminders.maintenance.schedule')}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          {t('reminders.deleteEquipment')}
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('reminders.deleteEquipment')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('common.confirmDelete')} {selectedEquipment?.brand} {selectedEquipment?.model}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentList;